import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Zap, RefreshCw, MessageSquare, Phone, CalendarCheck } from "lucide-react";

export default function MatchingPage() {
  const [matches, setMatches] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);

  const fetchData = async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/matching/recent"),
        api.get("/properties")
      ]);
      setMatches(m.data);
      setProperties(p.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchPropertyMatches = async (propId) => {
    setSelectedProp(propId);
    try {
      const { data } = await api.get(`/matching/property/${propId}`);
      setMatches(data);
    } catch (err) {
      console.error(err);
    }
  };

  const rerunMatching = async (propId) => {
    setRerunning(true);
    try {
      const { data } = await api.post(`/matching/rerun/${propId}`);
      alert(`Matching complete: ${data.matches_found} matches found`);
      fetchPropertyMatches(propId);
    } catch (err) {
      console.error(err);
    } finally {
      setRerunning(false);
    }
  };

  const generateMsg = async (match) => {
    try {
      const { data } = await api.post("/ai/generate-message", {
        context_type: "property_recommendation",
        lead_name: match.client_name,
        property_title: match.property_title,
        match_score: match.score
      });
      alert(`Message for ${match.client_name}:\n\n${data.message}`);
    } catch {}
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-[#2B8A3E] bg-green-50 border-green-200";
    if (score >= 50) return "text-[#E67700] bg-yellow-50 border-yellow-200";
    return "text-[#868E96] bg-gray-50 border-gray-200";
  };

  return (
    <div className="space-y-4" data-testid="matching-page">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Properties sidebar */}
        <div className="border border-border bg-white p-4">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">Properties</h3>
          {properties.length === 0 ? (
            <p className="text-sm text-[#868E96] py-4 text-center">No properties</p>
          ) : (
            <div className="space-y-1">
              <button
                onClick={() => { setSelectedProp(null); fetchData(); }}
                className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${!selectedProp ? "bg-[#002FA7] text-white" : "hover:bg-[#F1F3F5]"}`}
                data-testid="matching-all-properties"
              >
                All Recent Matches
              </button>
              {properties.map(p => (
                <button
                  key={p.id}
                  onClick={() => fetchPropertyMatches(p.id)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${selectedProp === p.id ? "bg-[#002FA7] text-white" : "hover:bg-[#F1F3F5]"}`}
                  data-testid={`matching-property-${p.id}`}
                >
                  <p className="font-medium truncate">{p.title}</p>
                  <p className={`text-xs ${selectedProp === p.id ? "text-white/70" : "text-[#868E96]"}`}>${p.price?.toLocaleString()}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Matches */}
        <div className="lg:col-span-3 space-y-4">
          {selectedProp && (
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#0A0A0A]">Matches for selected property</h3>
              <button
                onClick={() => rerunMatching(selectedProp)}
                disabled={rerunning}
                className="h-8 px-3 border border-border text-xs font-medium hover:bg-[#F1F3F5] flex items-center gap-2 rounded-sm disabled:opacity-50"
                data-testid="rerun-matching-button"
              >
                <RefreshCw size={12} className={rerunning ? "animate-spin" : ""} /> Re-run Matching
              </button>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-[#868E96] py-8 text-center">Loading matches...</p>
          ) : matches.length === 0 ? (
            <div className="border border-border bg-white p-12 text-center">
              <Zap size={32} className="mx-auto mb-3 text-[#868E96]" />
              <p className="text-sm text-[#868E96]">No matches found. Add properties and leads to start matching.</p>
            </div>
          ) : (
            <div className="border border-border bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-[#F8F9FA]">
                    <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Client</th>
                    <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Property</th>
                    <th className="text-right px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Score</th>
                    <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Breakdown</th>
                    <th className="text-right px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m, i) => (
                    <tr key={m.id || i} className="border-b border-border table-row-hover animate-fade-in" style={{ animationDelay: `${i * 0.02}s` }}>
                      <td className="px-4 py-3 font-medium text-[#0A0A0A]">{m.client_name}</td>
                      <td className="px-4 py-3 text-[#495057]">{m.property_title}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold border ${getScoreColor(m.score)}`}>
                          {m.score}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 flex-wrap">
                          {m.score_breakdown && Object.entries(m.score_breakdown).map(([key, val]) => (
                            <span key={key} className="text-[10px] px-1.5 py-0.5 bg-[#F1F3F5] border border-border text-[#495057]">
                              {key}: {val}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => generateMsg(m)} className="p-1.5 hover:bg-[#F1F3F5] rounded-sm" title="Generate message" data-testid={`match-message-${m.id || i}`}>
                            <MessageSquare size={14} className="text-[#002FA7]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
